/**
 * What the two money forms accept, and what they quietly clean up first.
 *
 * One module rather than a regex in each screen, because these two fields are
 * where a player's typing turns into an instruction to move real money, and
 * the rules had drifted apart: the withdrawal destination was checked for
 * `length >= 4` and nothing else, while the deposit reference took anything
 * from four to sixty-four characters including dots and dashes. Production
 * holds the evidence of both — an eleven-digit phone number, a nine-digit one,
 * and a scattering of six-character references that were never going to match
 * anything.
 *
 * ## Sanitise, then validate
 *
 * Each field has a `clean` and a `check`, and they do different jobs.
 * Cleaning fixes what is *not a mistake*: a phone number typed in groups, a
 * reference typed in lower case or pasted with a trailing space from a
 * receipt. Rejecting those would be pedantry aimed at someone who did nothing
 * wrong. Validating then rejects what genuinely cannot be right.
 *
 * The cleaned value is what gets submitted, so the server stores the canonical
 * form rather than whatever spacing the player happened to use — which is what
 * makes an exact reference match against Telebirr's own SMS possible at all.
 *
 * ## Mirrored, not shared
 *
 * The API enforces the same two rules independently (see
 * `http/routes/withdrawals.ts` and `http/routes/deposits.ts`). That is
 * deliberate duplication: this copy exists to *tell the player* what is wrong
 * while they are still looking at the field, and the server's exists because a
 * client cannot be trusted to have run. Neither is redundant, and the one that
 * actually guards the money is the far side.
 */

/** Ethiopian mobile: `09` then eight more digits. */
const TELEBIRR_NUMBER = /^09\d{8}$/;

/**
 * A Telebirr transaction number: ten characters, letters and digits, at least
 * one of each.
 *
 * Taken from the real ones rather than a spec — every reference telebirr's own
 * SMS has delivered (`telebirr_sms_events`) is exactly this shape:
 * DIA8M2ZPXQ, DIA1M1Q24B, DHS791Y1I9, DHR97U185D, DHP55DIY6J, DHP45DB7MG.
 *
 * The "at least one of each" clause is the operator's rule and holds for all
 * of them, though it rests on a smaller sample than the length does. If
 * telebirr ever issues an all-letter reference this would refuse a genuine
 * deposit, which is the one direction that costs a player something — worth
 * loosening to plain `[A-Z0-9]{10}` the first time that is reported.
 */
const TELEBIRR_REFERENCE = /^(?=.*[A-Z])(?=.*\d)[A-Z0-9]{10}$/;

/** Why a value was refused. Screens map these onto their own translations. */
export type FieldProblem = 'empty' | 'malformed';

/**
 * Spaces and dashes removed. A player writing "09 65 68 77 05" or
 * "0965-687-705" has not made a mistake, and a red banner would suggest they
 * had.
 */
export function cleanPhone(raw: string): string {
  return raw.replace(/[\s-]/g, '');
}

export function checkPhone(cleaned: string): FieldProblem | null {
  if (cleaned.length === 0) return 'empty';
  return TELEBIRR_NUMBER.test(cleaned) ? null : 'malformed';
}

/**
 * Upper-cased, with spaces and the separators receipts sometimes carry
 * stripped. Telebirr prints the reference in capitals; a player typing it in
 * lower case, or pasting it with a stray space, is not making a claim about a
 * different transaction.
 */
export function cleanReference(raw: string): string {
  return raw.replace(/[\s._-]/g, '').toUpperCase();
}

export function checkReference(cleaned: string): FieldProblem | null {
  if (cleaned.length === 0) return 'empty';
  return TELEBIRR_REFERENCE.test(cleaned) ? null : 'malformed';
}
